import { createRef, useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  selectDashboardIsLoading,
  selectDashboardLoadingState,
  selectGraphsEditable,
  selectGraphsReadOnly,
  selectGraphsStatus
} from '@/redux/selectors/dashboardSelectors';
import { LoadingStatuses } from '@/constants/constants';
import { useToast } from '../../_common/Toast';
import { Button, Grid, lighten } from '@mui/material';
import StatusCard from './StatusCard';
import { gsap } from 'gsap';
import ConfigureStatusCardsModal from './ConfigureStatusCardsModal';
import { StatusCard as StatusCardType } from '@/__configs/config_types';
import { ConfigContext } from '@/contextAPI/configContext';
import { CHART_STATUS_LABELS } from '../chartUtils';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppSelector } from '@/redux/hooks';

const StatusCards = () => {
  const { error } = useToast();
  const status = useAppSelector(selectGraphsStatus);
  const graphsEditable = useAppSelector(selectGraphsEditable);
  const graphsReadOnly = useAppSelector(selectGraphsReadOnly);
  const loading = useAppSelector(selectDashboardIsLoading);
  const loadingState = useAppSelector(selectDashboardLoadingState);
  const dashboardConfig = useContext(ConfigContext).dashboardConfig;
  const customizeDashboard = dashboardConfig.customizeDashboard;
  const [cardsToDisplay, setCardsToDisplay] = useState<StatusCardType[]>(dashboardConfig.statusCards || CHART_STATUS_LABELS);
  const refs = useRef(cardsToDisplay.map(() => createRef()));

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (status.editable && graphsEditable.statusCards && graphsEditable.statusCards.length > 0) {
      setCardsToDisplay(graphsEditable.statusCards);
    } else if (!status.editable && graphsReadOnly.statusCards && graphsReadOnly.statusCards.length > 0) {
      setCardsToDisplay(graphsReadOnly.statusCards);
    }
  }, [status.editable, graphsEditable.statusCards]);

  useEffect(() => {
    if (loadingState === LoadingStatuses.rejected) {
      error('There was a problem loading dashboard data.');
      return;
    }
    if (!loadingState || loadingState === LoadingStatuses.pending) return;
    const duration = 1; // seconds
    const targets = refs.current.map((ref) => ref.current).filter((el) => el);
    const innerTargets = targets.map((target: any) => target.el);
    const domRefs = innerTargets.map((el) => el.current);
    // we use an imperative handle to trigger a fn on each child component
    const handleOnUpdate = (counter: any) => targets.forEach((target) => (target as any).onUpdate(counter));
    const handleOnComplete = () => targets.forEach((target) => (target as any).onComplete());
    const counter = { val: 0 };
    // animate the numbers up
    gsap.set(counter, { val: 0, onUpdate: () => handleOnUpdate(counter.val) });
    gsap.to(counter, { duration, val: 1, onUpdate: () => handleOnUpdate(counter.val), onComplete: handleOnComplete });
    // animate the DOM scale and opacity
    gsap.set([domRefs], { scale: 0, opacity: 0 });
    gsap.to([domRefs], { duration, scale: 1, opacity: 1, ease: 'back', stagger: 0.15 });

    return () => gsap.killTweensOf([domRefs, counter]);
  }, [loading, cardsToDisplay, status.editable]);

  return (
    <Wrapper>
      {status.editable && (
        <ConfigureButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setIsOpen(true)}
          startIcon={<PolarisIcon height={15} width={15} icon="settings_filled" />}
        >
          Configure
        </ConfigureButton>
      )}
      <StyledGrid
        container
        spacing={2.5}
        disabled={+status.editable}
        // sx={{ mb: 3, alignItems: 'center', justifyContent: 'center', position: 'relative', opacity: status.editable ? '0.4' : 1 }}
      >
        {cardsToDisplay.map((card, i) => (
          <Grid item key={`status-cards-${i}`} xs={12} md={cardsToDisplay.length > 6 ? 2 : 12 / cardsToDisplay.length}>
            <StatusCard
              key={`${card.status}_${i}`}
              card={card}
              filter={card.filter}
              loading={loading}
              ref={refs.current[i]}
              totalCards={cardsToDisplay.length}
            />
          </Grid>
        ))}
      </StyledGrid>
      {customizeDashboard && <ConfigureStatusCardsModal isOpen={isOpen} closeWindow={() => setIsOpen(false)} data={graphsEditable.statusCards} />}
    </Wrapper>
  );
};

export default StatusCards;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledGrid = styled(Grid)<{ disabled: number }>`
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacing(0.5)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  > .MuiGrid-root:nth-of-type(1) {
    padding-left: 0px;
  }

  > .MuiGrid-root {
    padding-top: 0px;
    padding-bottom: 0px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    > .MuiGrid-root {
      padding-left: 0px;
    }

    row-gap: ${({ theme }) => theme.spacing(2)};
  }
`;

const ConfigureButton = styled(Button)`
  position: absolute;
  z-index: 1;
  top: -45px;
  right: 0;

  width: 125px;
  white-space: nowrap;
  // background: ${({ theme }) => lighten(theme.palette.tertiary.main, theme.palette.mode === 'dark' ? 0.5 : 0.6)};

  &:hover {
    // background: ${({ theme }) => (theme.palette.mode === 'dark' ? lighten(theme.palette.tertiary.main, 0.2) : theme.palette.tertiary.main)};
  }
`;
